import { Params, ParamMap } from '@angular/router';
import { UserSearchCriteria } from '../../services/search.service';

export function criteriaToQueryParams(criteria: UserSearchCriteria): Params {
  const loc = criteria.selectedLocation || {};
  const locId = loc.id ?? loc.value;
  const params: Params = {
    locId: locId != null ? String(locId) : '',
    locType: loc.type ? String(loc.type) : '',
    locLabel: loc.label ? String(loc.label) : (criteria.location || ''),
    a1: String(criteria.adults ?? 2),
    c1: String(criteria.children ?? 0),
    rooms: String(criteria.rooms ?? 1),
  };

  if (loc.parentId != null && loc.parentId !== 0) {
    params['locParent'] = String(loc.parentId);
  }
  if (loc.masterHotelId != null && loc.masterHotelId !== 0) {
    params['locMaster'] = String(loc.masterHotelId);
  }

  const checkIn = toIsoDate(criteria.checkInDate);
  const checkOut = toIsoDate(criteria.checkOutDate);
  if (checkIn) params['checkIn'] = checkIn;
  if (checkOut) params['checkOut'] = checkOut;

  if (criteria.childrenAges && criteria.childrenAges.length > 0) {
    params['ages1'] = criteria.childrenAges.join(',');
  }

  if ((criteria.rooms ?? 1) > 1) {
    params['a2'] = String(criteria.room2Adults ?? 1);
    params['c2'] = String(criteria.room2Children ?? 0);
    if (criteria.room2ChildrenAges && criteria.room2ChildrenAges.length > 0) {
      params['ages2'] = criteria.room2ChildrenAges.join(',');
    }
  }

  return params;
}

export function queryParamsToCriteria(params: ParamMap): UserSearchCriteria | null {
  const locId = params.get('locId');
  const locType = params.get('locType');
  const locLabel = params.get('locLabel');

  // Minimum gerekli: location bilgisi
  if (!locId || !locType || !locLabel) {
    return null;
  }

  const locIdNum = tryParseInt(locId);
  const selectedLocation: any = {
    id: locIdNum,
    value: locIdNum,
    type: locType,
    label: locLabel,
  };

  const locParent = params.get('locParent');
  if (locParent) selectedLocation.parentId = tryParseInt(locParent);

  const locMaster = params.get('locMaster');
  if (locMaster) selectedLocation.masterHotelId = tryParseInt(locMaster);

  const checkInStr = params.get('checkIn');
  const checkOutStr = params.get('checkOut');
  const checkInDate = checkInStr ? parseDate(checkInStr) : null;
  const checkOutDate = checkOutStr ? parseDate(checkOutStr) : null;

  return {
    location: locLabel,
    selectedLocation,
    checkInDate,
    checkOutDate,
    adults: parseIntOr(params.get('a1'), 2),
    children: parseIntOr(params.get('c1'), 0),
    rooms: parseIntOr(params.get('rooms'), 1),
    room2Adults: parseIntOr(params.get('a2'), 1),
    room2Children: parseIntOr(params.get('c2'), 0),
    childrenAges: parseAges(params.get('ages1')),
    room2ChildrenAges: parseAges(params.get('ages2')),
  };
}

export function queryParamsEqual(a: Params | null | undefined, b: Params | null | undefined): boolean {
  const aObj = a || {};
  const bObj = b || {};
  const aKeys = Object.keys(aObj);
  const bKeys = Object.keys(bObj);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every(k => String(aObj[k]) === String(bObj[k]));
}

function toIsoDate(d: Date | string | null | undefined): string {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseDate(s: string): Date | null {
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function tryParseInt(s: string): number {
  const n = parseInt(s, 10);
  return isNaN(n) ? 0 : n;
}

function parseIntOr(s: string | null, fallback: number): number {
  if (!s) return fallback;
  const n = parseInt(s, 10);
  return isNaN(n) ? fallback : n;
}

function parseAges(s: string | null): number[] {
  if (!s) return [];
  return s.split(',').map(x => parseInt(x.trim(), 10)).filter(n => !isNaN(n));
}
